import React, { Suspense } from 'react';

import PageBuilder, { ImageForRef } from '$/components/ui/Input/PageBuilder';
import { PageBuilderState, PageMode } from '$/components/ui/Input/PageBuilder.types';
import { RegistrationFormSettings, EwebinarSessions } from '$/GraphQL/schema/schema';
import { renderHTML } from '$/utils/renderHTML';
import { ClassNames } from '@emotion/react';
import { readableColorOnBackground } from '$/utils/colors';
import { useWindowSizeInfo } from '$/utils/useWindowSize';
import { WidgetInfo, WidgetType } from '$/widget/src/interface';
import {
  useSSOAuth,
  SSOLoginButton,
} from '$/sections/Home/Profile/TeamIntegrations/Info/SSO/SSOLogin';
import { markdownToHTML } from '$/shared/frontend/utils/MarkdownConverter';
import { MAX_CHARS_REGISTER_BUTTON } from '$/components/ui/Input/CTAButton/variables';
import { langDirClassname } from '$/shared/frontend/utils/i18n';
import DangerouslySetInnerHTML from '$/components/ui/EditableInput/DangerouslySetInnerHTML';
import { I18n } from '@lingui/core';
import { useWebinarContext, WebinarState } from '../../context';
import { PoweredByEWebinar } from '$/components/Logo';
import { Trans, t } from '@lingui/macro';
import { ArrowRightIcon } from '$/images/FontAwesomeIcons';
import { ButtonSettings } from './types';
import Button from '$/components/ui/Button';
import Form from '$/components/ui/Form';
import Modal from '$/components/ui/Modal';
import RegistrationPageFormModal from './RegistrationPageFormModal';
import { useRegistrationFormFields } from './useRegistrationFormFields';

const EditPageSubmit = React.lazy(() => import('$/sections/Home/WebinarEditor/EditPageSubmit'));

export const CloseButton: React.FC<{ onClose: () => void; className?: string }> = ({
  onClose,
  className,
}) => (
  <Button
    className={`button-close w-auto text-sm outline-none text-gray-23 z-sso-close-btn ${className}`}
    onClick={(): void => onClose()}
  >
    Cancel
  </Button>
);

const SkipButton: React.FC<{ onClose: () => void; className?: string }> = ({
  onClose,
  className,
}) => (
  <Button
    className={`button-close w-auto text-2xl outline-none text-white z-sso-close-btn px-0 mr-2e ${className}`}
    onClick={(): void => onClose()}
  >
    <Trans>
      Skip <ArrowRightIcon className='text-xl md:text-2xl ml-1/2e' />
    </Trans>
  </Button>
);

interface RegistrationFormFieldsProps {
  i18n: I18n;
  registrationFormSettings: RegistrationFormSettings;
  state: PageBuilderState;
  isWidget?: boolean;
  widgetType?: WidgetType;
  onClose?: () => void;
  showConditionsPopup: boolean;
  setShowConditionsPopup: (show: boolean) => void;
  sessions?: EwebinarSessions;
  widgetInfo?: WidgetInfo;
  submitting?: boolean;
  captchaRef?: React.RefObject<HTMLDivElement>;
  hideConsentCheckbox?: boolean;
  title?: string;
  buttonSettings?: ButtonSettings;
  isInVideoRegistration?: boolean;
  showSkipButton?: boolean;
  unlockedModal?: boolean;
}

const RegistrationFormFields: React.FC<RegistrationFormFieldsProps> = ({
  i18n,
  state,
  onClose,
  isWidget,
  widgetType,
  showConditionsPopup,
  sessions,
  widgetInfo,
  submitting,
  captchaRef,
  hideConsentCheckbox,
  title,
  buttonSettings,
  isInVideoRegistration,
  showSkipButton,
  unlockedModal,
}) => {
  const { md: isWidthMd } = useWindowSizeInfo();

  const { mode, ewebinar, language } = state;
  const { authorized, requireSSO } = useSSOAuth(state.set);
  const isFloatInWidget = widgetType && [WidgetType.FloatIn].includes(widgetType);

  // exclude interaction property from the dropdown to avoid the issue of overwrite data in attendeeRecord
  const webinarState = useWebinarContext();
  const draftWebinar: WebinarState['draftWebinar'] | undefined = webinarState?.draftWebinar;
  const interactions = draftWebinar?.interactions;

  const interactionProperties = interactions
    ?.filter((i) => i.propertyName)
    .map((p) => p.propertyName?.toLowerCase());

  const dir = mode === PageMode.edit ? 'ltr' : langDirClassname(language);
  const registerButtonTheme = React.useMemo(() => {
    if (!ewebinar) return undefined;
    const bgButtonColor = buttonSettings?.buttonColor ?? ewebinar?.themeSettings.actionColor;
    const buttonColor = readableColorOnBackground(bgButtonColor);

    return {
      fg: buttonColor,
      fgHover: buttonColor,
      bg: bgButtonColor,
      bgHover: bgButtonColor,
    };
  }, [buttonSettings?.buttonColor, ewebinar]);

  const { fieldsElements } = useRegistrationFormFields({
    i18n,
    requireSSO,
    excludeProperties: interactionProperties?.filter(Boolean) as string[],
    hideConsentCheckbox,
    isInVideoRegistration,
    sessions,
    showConditionsPopup,
  });

  return (
    <ClassNames>
      {({ css, cx }): React.ReactElement => {
        if (!ewebinar || !state.set) return <></>;

        return (
          <RegistrationPageFormModal
            isWidget={isWidget}
            onClose={onClose}
            unlockedModal={unlockedModal}
          >
            {/* <DevTool control={control} /> */}
            {mode !== PageMode.edit &&
              state.set.setSettings.accessControl?.requireSSO &&
              !authorized && (
                <div
                  className={cx(
                    `absolute w-full h-full flex items-center justify-center z-sso-form px-8 rounded-lg`,
                    css`
                      background-color: rgba(0, 0, 0, 0.2);
                    `
                  )}
                >
                  <SSOLoginButton
                    className={cx(
                      `h-12 w-full px-1_5e py-1/2e text-lg mb-2e`,
                      css`
                        color: ${readableColorOnBackground(ewebinar.themeSettings.primaryColor)};
                        background-color: ${ewebinar.themeSettings.primaryColor};
                        font-family: '${ewebinar.themeSettings.font ?? 'unset'}';
                      `
                    )}
                    set={state.set}
                    parentUrl={widgetInfo?.parentUrl}
                  >
                    {t(i18n)`Login to register`}
                  </SSOLoginButton>
                </div>
              )}

            <RegistrationPageFormModal.Header authorized={authorized} isWidget={isWidget}>
              <div className='flex flex-col w-full'>
                {mode !== PageMode.edit && !isWidthMd && !isWidget && !isInVideoRegistration && (
                  <div className='flex w-full justify-between items-center bg-white md:hidden'>
                    {ewebinar && ewebinar.logoMediaRef ? (
                      <ImageForRef
                        className='logo w-auto object-contain max-w-11e md:max-w-13e h-2_5e mt-0'
                        asset={{
                          entity: 'ewebinar',
                          name: 'logo',
                          container: {
                            width: 300,
                            height: 75,
                          },
                        }}
                        value={ewebinar.logoMediaRef}
                      />
                    ) : (
                      <div />
                    )}
                    {onClose && !isInVideoRegistration && <CloseButton onClose={onClose} />}
                  </div>
                )}
                <div className={`flex flex-col w-full items-start`}>
                  {isFloatInWidget ? (
                    <span className='leading-normal'>{ewebinar.title}</span>
                  ) : isInVideoRegistration ? (
                    <h2 className='form-title w-full text-white text-4xl md:text-6xl text-center leading-normal mx-auto mb-1e md:mb-2e'>
                      {renderHTML(markdownToHTML(title ?? t`Register to continue watching`))}
                    </h2>
                  ) : (
                    <Form.RawField
                      name='title'
                      className={cx(
                        isWidthMd || isWidget ? 'text-gray-0 py-0' : 'text-gray-0 py-1e',
                        css`
                          ${!isWidthMd && !isWidget
                            ? `
                            .public-DraftStyleDefault-block {
                              text-align: center;
                            }
                            `
                            : ''}
                        `
                      )}
                      component={
                        <PageBuilder.TextEdit
                          disabled={mode !== PageMode.edit}
                          state={state}
                          showInlineToolbar={false}
                          isRtl={dir === 'rtl'}
                        />
                      }
                    />
                  )}
                </div>
              </div>
              {onClose && !isInVideoRegistration && (
                <Modal.Close
                  onClick={onClose}
                  className={`
                  ${!authorized ? 'z-sso-close-btn' : ''}
                  ${isWidthMd || isWidget ? '' : 'hidden'}
                `}
                />
              )}
            </RegistrationPageFormModal.Header>
            <RegistrationPageFormModal.Body>
              <div
                className={cx(
                  '',
                  css`
                    .grecaptcha-badge {
                      visibility: hidden;
                    }
                  `
                )}
              >
                <div ref={captchaRef} />
              </div>
              {isFloatInWidget && authorized && (
                <Form.RawField
                  name='title'
                  className={cx('mb-1/2e')}
                  component={
                    <PageBuilder.TextEdit
                      value={''}
                      disabled={mode !== PageMode.edit}
                      state={state}
                      showInlineToolbar={false}
                      isRtl={dir === 'rtl'}
                    />
                  }
                />
              )}
              {fieldsElements}
            </RegistrationPageFormModal.Body>
            <RegistrationPageFormModal.Footer i18n={i18n}>
              <PoweredByEWebinar
                i18nProvided={i18n}
                className={css`
                  @media (max-width: 767px) {
                    display: none;
                  }
                `}
                hideBranding={isInVideoRegistration || state.set.team.settings.hideBranding}
                teamId={state.set.team.id}
              />
              {isInVideoRegistration && showSkipButton && onClose && (
                <SkipButton onClose={onClose} />
              )}
              {mode !== PageMode.edit ? (
                <div className='flex justify-end items-center md:justify-end mb-0 w-full'>
                  <Form.SubmitButton
                    className={cx(
                      `h-12 w-full md:w-auto px-1_5e py-2 text-lg`,
                      css`
                        font-family: '${ewebinar.themeSettings.font ?? 'unset'}';
                      `,
                      mode === PageMode.preview ? 'opacity-100' : ''
                    )}
                    theme={registerButtonTheme}
                    disabled={!authorized || mode === PageMode.preview}
                    submitting={submitting}
                  >
                    <DangerouslySetInnerHTML
                      markdownString={
                        buttonSettings?.buttonText ??
                        ewebinar.registrationFormSettings?.registerButton ??
                        'Register'
                      }
                    />
                  </Form.SubmitButton>
                </div>
              ) : (
                <>
                  <div
                    className={cx(
                      'flex justify-end items-center',
                      css`
                        .ui.dropdown .menu > .item {
                          padding: 0 !important;
                        }
                      `
                    )}
                  >
                    <PageBuilder.Button
                      name='registerButton'
                      state={state}
                      className='px-1_5e py-2'
                      maxChars={MAX_CHARS_REGISTER_BUTTON}
                      linkToolbarDisabled
                      theme={registerButtonTheme}
                    />
                  </div>
                  {mode === PageMode.edit && (
                    <Suspense>
                      <EditPageSubmit />
                    </Suspense>
                  )}
                </>
              )}
            </RegistrationPageFormModal.Footer>
          </RegistrationPageFormModal>
        );
      }}
    </ClassNames>
  );
};

export default RegistrationFormFields;
